import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismBadge,
  PrismButton,
  PrismIcon,
  PrismNumberInput,
  PrismSelectInput,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { modifier } from 'ember-modifier';
import type { RoomOffsetHierarchy } from 'lumen/models/recommendation-strategy';
import outsideClickHandler from 'lumen/modifiers/outside-click-handler';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { FEATURE_TOGGLES } from 'lumen/utils/constants/feature-toggle-constants';
import { resolveOffsetValue } from 'lumen/utils/pricing-assistant/room-hierarchy-settings';

import styles from './room-item.module.css';

function countRoomsInSubtree(room: RoomOffsetHierarchy): number {
  return (
    1 +
    Object.values(room.children).reduce(
      (count, child) => count + countRoomsInSubtree(child),
      0,
    )
  );
}

export type RoomSelectOption = {
  badge?: string;
  badgeType?: 'neutral';
  label: string;
};

interface SettingsRecommendationsRoomHierarchyRoomItemSignature {
  Args: {
    activeRoomCode: string | null;
    editingRoomCode: string | null;
    isEditMode: boolean;
    onActivate: (roomCode: string | null) => void;
    onAddChildRoom: (parentRoomCode: string) => void;
    onCommitRoom: (
      oldRoomCode: string,
      newRoomCode: string,
      offsetValue: number | null,
    ) => void;
    onRemoveRoom: (roomCode: string) => void;
    onRoomEdit: (roomCode: string | null) => void;
    parentRoomCode?: string;
    pathRoomCode?: string;
    room: { roomCode: string; roomHierarchy: RoomOffsetHierarchy };
    roomSelectOptions: RoomSelectOption[];
  };
}

export default class RoomItem extends Component<SettingsRecommendationsRoomHierarchyRoomItemSignature> {
  @service declare alerting: Services['alerting'];

  @service declare featureToggle: Services['feature-toggle'];

  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;

  @service declare intl: Services['intl'];

  @tracked hasOffsetRangeError = false;

  // holds the room code the user has selected in the dropdown but not yet confirmed
  @tracked localPendingRoomCode: string | null = null;
  @tracked maxOffsetValue: number | null = null;
  @tracked offsetValue: number | null = null;

  // Re-runs whenever localPendingRoomCode changes, so the input
  // is focused the moment a room is selected from the dropdown.
  focusOffsetInput = modifier((element: HTMLElement): void => {
    if (!this.localPendingRoomCode) {
      return;
    }

    const input = element.querySelector('input');

    input?.focus();
  });

  resetRoomEditMode = modifier(
    (
      _element: Element,
      [isEditMode, isRoomEditMode, roomCode]: [boolean, boolean, string],
    ): void => {
      if (!isEditMode || (!isRoomEditMode && roomCode)) {
        this.localPendingRoomCode = null;
      }
    },
  );

  get childCount(): number {
    return countRoomsInSubtree(this.args.room.roomHierarchy) - 1;
  }

  get currencySymbol(): string {
    return this.hotelAndMarketService.hotel.currencySymbol;
  }

  get currentRoomCode(): string {
    return this.args.room.roomCode;
  }

  get displayMaxOffsetValue(): number {
    return resolveOffsetValue(this.maxOffsetValue);
  }

  // Non-edit display uses 0 for an empty field, consistent with what save persists.
  get displayOffsetValue(): number {
    return resolveOffsetValue(this.offsetValue);
  }

  get hasRoomChildren(): boolean {
    return Boolean(Object.keys(this.args.room.roomHierarchy.children).length);
  }

  get isActive(): boolean {
    return this.args.activeRoomCode === this.currentRoomCode;
  }

  get isBaseRoom(): boolean {
    return (
      this.currentRoomCode ===
      this.hotelAndMarketService.hotel.pricingIntegration
        .pms_leading_room_pms_id
    );
  }

  /** True when this room is an ancestor of the active room in the navigation path (light-blue highlight). */
  get isInPath(): boolean {
    return this.args.pathRoomCode === this.currentRoomCode;
  }

  get isMinMaxOffsetEnabled(): boolean {
    return this.featureToggle.hasFeatureToggle(
      FEATURE_TOGGLES.PRICING_MANAGER_CASCADING_MINMAX_ROOM_OFFSETS,
    );
  }

  get isOffsetRangeInvalid(): boolean {
    return (
      resolveOffsetValue(this.maxOffsetValue) <
      resolveOffsetValue(this.offsetValue)
    );
  }

  /*
   * editingRoomCode is a single value owned by the table parent
   * only one room across the entire hierarchy can be in edit mode at a time.
   */
  get isRoomEditMode(): boolean {
    return this.args.editingRoomCode === this.currentRoomCode;
  }

  get selectedRoomOption(): RoomSelectOption | undefined {
    const roomCode = this.localPendingRoomCode ?? this.currentRoomCode;
    return this.args.roomSelectOptions.find((room) => room.label === roomCode);
  }

  get showRoomSelect(): boolean {
    return (
      this.args.isEditMode && (!this.currentRoomCode || this.isRoomEditMode)
    );
  }

  constructor(
    owner: Owner,
    args: SettingsRecommendationsRoomHierarchyRoomItemSignature['Args'],
  ) {
    super(owner, args);
    // Newly created room items start with no roomCode and no offset;
    // therefore the offset input is cleared with null so the placeholder "0"
    // shows instead of a real zero the user would have to delete before typing.
    this.offsetValue =
      this.currentRoomCode === '' ? null : args.room.roomHierarchy.offset.value;

    if (this.isMinMaxOffsetEnabled) {
      this.maxOffsetValue =
        this.currentRoomCode === ''
          ? null
          : args.room.roomHierarchy.maximum_offset!.value;
    }
  }

  private async confirmAndRemove(): Promise<void> {
    const count = countRoomsInSubtree(this.args.room.roomHierarchy);
    const confirmed = await this.alerting.showWarningAlert({
      dangerMode: true,
      title: this.intl.t(
        'pricing_assistant.recommendations.settings.room_hierarchy.remove_room_title',
      ),
      text: this.intl.t(
        'pricing_assistant.recommendations.settings.room_hierarchy.remove_room_description',
        { count: this.childCount },
      ),
      buttons: {
        cancel: {
          text: this.intl.t('general.cancel'),
        },
        confirm: {
          text: this.intl.t(
            'pricing_assistant.recommendations.settings.room_hierarchy.remove_n_rooms',
            { count },
          ),
        },
      },
    });

    if (confirmed) {
      this.args.onRemoveRoom(this.currentRoomCode);
    }
  }

  @action
  activate(): void {
    this.args.onActivate(this.isActive ? null : this.currentRoomCode);
  }

  @action
  handleClickOutside(): void {
    if (this.isMinMaxOffsetEnabled && this.isOffsetRangeInvalid) {
      this.hasOffsetRangeError = true;
      return;
    }

    this.hasOffsetRangeError = false;

    const resolvedOffsetCurrent = resolveOffsetValue(this.offsetValue);

    const resolvedOffsetOriginal = resolveOffsetValue(
      this.args.room.roomHierarchy.offset.value,
    );

    const roomCodeChanged = this.localPendingRoomCode !== null;

    const offsetChanged = resolvedOffsetCurrent !== resolvedOffsetOriginal;

    if (roomCodeChanged || offsetChanged) {
      this.args.onCommitRoom(
        this.currentRoomCode,
        this.localPendingRoomCode ?? this.currentRoomCode,
        resolvedOffsetCurrent,
      );
    }

    this.localPendingRoomCode = null;
    this.args.onRoomEdit(null);
  }

  @action
  handleMaxOffsetChange(value: number | null): void {
    this.maxOffsetValue = value;
  }

  @action
  handleMinOffsetChange(value: number | null): void {
    this.offsetValue = value;
  }

  @action
  handleOffsetKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter') {
      return;
    }

    if (!this.localPendingRoomCode && !this.currentRoomCode) {
      return;
    }

    if (this.offsetValue === null) {
      // pressing Enter on an empty offset field defaults to 0 rather than
      // leaving it blank.
      this.offsetValue = 0;
    }

    if (this.isMinMaxOffsetEnabled && this.isOffsetRangeInvalid) {
      this.hasOffsetRangeError = true;
      return;
    }

    this.hasOffsetRangeError = false;

    this.args.onCommitRoom(
      this.currentRoomCode,
      this.localPendingRoomCode ?? this.currentRoomCode,
      this.offsetValue,
    );

    this.localPendingRoomCode = null;
  }

  @action
  handlePencilClick(): void {
    this.args.onRoomEdit(this.currentRoomCode);
  }

  @action
  handleRemoveClick(): void {
    if (this.hasRoomChildren) {
      void this.confirmAndRemove();
    } else {
      this.args.onRemoveRoom(this.currentRoomCode);
    }
  }

  @action
  handleRoomCodeChange(option: RoomSelectOption): void {
    if (this.args.parentRoomCode) {
      // Child rooms have an offset to fill, so defer the commit until Enter.
      this.localPendingRoomCode = option.label;
    } else {
      // Root rooms have no offset — commit the rename immediately with null offset.
      this.args.onCommitRoom(this.currentRoomCode, option.label, null);
    }
  }

  @action
  onAddChildRoom(): void {
    this.args.onAddChildRoom(this.currentRoomCode);
  }

  <template>
    {{#if this.showRoomSelect}}
      <div
        class="{{styles.room-edit-wrapper}}
          {{if @room.roomCode styles.room-context}}"
        {{outsideClickHandler this.handleClickOutside}}
        {{this.resetRoomEditMode
          @isEditMode
          this.isRoomEditMode
          @room.roomCode
        }}
      >
        <PrismSelectInput
          @onChange={{this.handleRoomCodeChange}}
          @options={{@roomSelectOptions}}
          @placeholder={{t
            "pricing_assistant.recommendations.settings.room_hierarchy.select_room"
          }}
          @unsafe_initiallyOpened={{unless @room.roomCode true}}
          @unsafe_matchTriggerWidth={{true}}
          @value={{this.selectedRoomOption}}
          data-test-id="dropdown-room-options"
        />
        {{#if @parentRoomCode}}
          <div class={{styles.offset-edit}} {{this.focusOffsetInput}}>
            <span class="text-200--regular {{styles.parent-label}}">=
              {{@parentRoomCode}}
              +</span>
            <PrismNumberInput
              @hasErrors={{this.hasOffsetRangeError}}
              @inlineLabel={{this.currencySymbol}}
              @isSmall={{true}}
              @isTouched={{true}}
              @min={{0}}
              @onValueChange={{this.handleMinOffsetChange}}
              @placeholder="0"
              @removePadding={{true}}
              @value={{this.offsetValue}}
              data-test-id="input-offset-min"
              {{on "keydown" this.handleOffsetKeydown}}
            />
            {{#if this.isMinMaxOffsetEnabled}}
              <span class="text-200--regular {{styles.parent-label}}">{{t
                  "pricing_assistant.recommendations.settings.room_hierarchy.offset_to_separator"
                }}</span>
              <PrismNumberInput
                @hasErrors={{this.hasOffsetRangeError}}
                @inlineLabel={{this.currencySymbol}}
                @isSmall={{true}}
                @isTouched={{true}}
                @min={{0}}
                @onValueChange={{this.handleMaxOffsetChange}}
                @placeholder="0"
                @removePadding={{true}}
                @value={{this.maxOffsetValue}}
                data-test-id="input-offset-max"
                {{on "keydown" this.handleOffsetKeydown}}
              />
            {{/if}}
          </div>
          {{#if this.hasOffsetRangeError}}
            <div
              class="text-200--medium text-critical-default flex flex-row gap-100"
              data-test-id="offset-range-error"
            >
              <PrismIcon
                @color="critical"
                @name="circle-error-filled"
                @size="small"
              />
              {{t
                "pricing_assistant.recommendations.settings.room_hierarchy.max_offset_below_min"
              }}
            </div>
          {{/if}}
        {{/if}}
      </div>

    {{else}}
      <div
        class="{{styles.room-view-wrapper}}
          {{if @isEditMode styles.room-view-wrapper-with-actions}}
          {{if
            this.isActive
            styles.room-view-wrapper-active
            (if this.isInPath styles.room-context)
          }}"
        data-test-id="container-room-item"
        {{this.resetRoomEditMode
          @isEditMode
          this.isRoomEditMode
          @room.roomCode
        }}
      >
        <button
          class={{styles.room-info}}
          type="button"
          {{on "click" this.activate}}
        >
          <span class={{styles.room-code-row}}>
            <span class={{styles.room-code}}>{{@room.roomCode}}</span>
            {{#if this.isBaseRoom}}
              <PrismBadge
                @isSmall={{true}}
                @label={{t
                  "pricing_assistant.settings.panel.section.rooms.leading_room"
                }}
                @type="neutral"
                class={{styles.base-room-badge}}
              />
            {{/if}}
          </span>
          {{#if @parentRoomCode}}
            <span
              class="text-200--regular {{styles.offset-read}}"
              data-test-id="display-offset"
            >
              <span>=</span>
              <span>{{@parentRoomCode}}</span>
              <span>+</span>
              <span>{{this.currencySymbol}}{{this.displayOffsetValue}}</span>
              {{#if this.isMinMaxOffsetEnabled}}
                <span>{{t
                    "pricing_assistant.recommendations.settings.room_hierarchy.offset_to_separator"
                  }}</span>
                <span
                >{{this.currencySymbol}}{{this.displayMaxOffsetValue}}</span>
              {{/if}}
            </span>
          {{/if}}
        </button>
        {{#if this.hasRoomChildren}}
          <span class={{styles.child-indicator}}>
            <PrismBadge
              @isSmall={{true}}
              @label={{this.childCount}}
              @type="neutral"
              data-test-id="badge-child-count"
            />
            <PrismIcon @name="chevron-right" @size="small" />
          </span>
        {{/if}}
        {{#if @isEditMode}}
          <div class={{styles.actions}}>
            <PrismButton
              @buttonType="ghost"
              @icon="edit-pencil"
              @onClick={{this.handlePencilClick}}
              @size="small"
              data-test-id="button-edit-pencil"
            />
            {{#if @parentRoomCode}}
              <PrismButton
                @buttonType="ghost"
                @icon="bin"
                @onClick={{this.handleRemoveClick}}
                @size="small"
                data-test-id="button-remove-room"
              />
            {{/if}}
            <PrismButton
              @buttonType="secondary"
              @iconBefore="plus"
              @label={{t
                "pricing_assistant.recommendations.settings.room_hierarchy.add_child_room"
              }}
              @onClick={{this.onAddChildRoom}}
              @size="small"
              data-test-id="button-add-child"
            />
          </div>
        {{/if}}
      </div>
    {{/if}}
  </template>
}
